// Minimal obs-websocket v5 protocol client (OBS Studio 28+). Hand-rolled the
// same way _lib/supabase.js hand-rolls PostgREST — the whole protocol we need
// (identify handshake, a dozen request types, request batches, a few events)
// is smaller than the obs-websocket-js dependency, and Web Crypto covers the
// auth challenge.
// Protocol reference:
// https://github.com/obsproject/obs-websocket/blob/master/docs/generated/protocol.md
//
// One instance = one connection attempt's lifetime. Reconnection policy lives
// in obsControl.js — when the socket closes, this object is dead; make a new
// one. That keeps every handler here free of "are we the current socket?"
// checks.

const OP = {
  HELLO: 0,
  IDENTIFY: 1,
  IDENTIFIED: 2,
  EVENT: 5,
  REQUEST: 6,
  REQUEST_RESPONSE: 7,
  REQUEST_BATCH: 8,
  REQUEST_BATCH_RESPONSE: 9,
};

// RequestBatchExecutionType: OBS runs a batch's requests one after another
// in real time, honouring its Sleep requests (sleepMillis), which is how the
// console has OBS play an animation on its own clock (obsStage.js).
export const BATCH_SERIAL_REALTIME = 0;

// EventSubscription bits: General (1<<0) | Scenes (1<<2) | Inputs (1<<3) |
// SceneItems (1<<7) | Ui (1<<10) | InputVolumeMeters (1<<16) |
// InputActiveStateChanged (1<<17).
// Scenes carries program/preview scene changes + scene list edits; Ui carries
// StudioModeStateChanged; Inputs carries mute and volume changes and
// SceneItems sources shown or hidden in OBS itself, so the console's sound
// controls and side swap follow what the operator does in OBS. The meters are
// a high-volume subscription (every ~50 ms per input with audio) for the
// console's level bars; on a local socket that is a few kilobytes a second.
// InputActiveStateChanged says when a source goes on or off air, which is
// when OBS's mixer shows or drops a scene's own audio source; the console's
// mixer follows it.
const EVENT_SUBSCRIPTIONS = (1 << 0) | (1 << 2) | (1 << 3) | (1 << 7) | (1 << 10) | (1 << 16) | (1 << 17);

// WebSocketCloseCode.AuthenticationFailed — the one close code the caller
// must treat specially (wrong password ≠ OBS closed; retrying is pointless).
export const CLOSE_AUTH_FAILED = 4009;

const CONNECT_TIMEOUT_MS = 8000;
const REQUEST_TIMEOUT_MS = 5000;

const b64 = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf)));
const sha256b64 = async (text) =>
  b64(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text)));

// secret = b64(sha256(password + salt)); authentication = b64(sha256(secret + challenge))
async function buildAuthString(password, { challenge, salt }) {
  const secret = await sha256b64(`${password}${salt}`);
  return sha256b64(`${secret}${challenge}`);
}

const newRequestId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 14)}`;

export class ObsSocket {
  constructor({ url, password = '', onEvent = null, onClose = null, connectTimeoutMs = CONNECT_TIMEOUT_MS }) {
    this.url = url;
    this.password = password;
    this.connectTimeoutMs = connectTimeoutMs;
    this.onEvent = onEvent;
    this.onClose = onClose;
    this._ws = null;
    this._pending = new Map(); // requestId → { resolve, reject, timer }
    this._closed = false;
  }

  // Resolves once the server sends Identified; rejects if the socket closes
  // or times out first. A rejection here still fires onClose (single exit
  // path for the manager's reconnect logic).
  connect() {
    return new Promise((resolve, reject) => {
      let ws;
      try {
        ws = new WebSocket(this.url);
      } catch (err) {
        // Malformed URL — WebSocket() throws synchronously.
        this._closed = true;
        reject(err);
        this.onClose?.({ code: 0, reason: String(err?.message ?? err) });
        return;
      }
      this._ws = ws;

      let settled = false;
      const timer = setTimeout(() => {
        // Never identified — a firewalled LAN IP can sit in CONNECTING for
        // ages; force the close path instead of hanging the manager.
        if (!settled) ws.close();
      }, this.connectTimeoutMs);

      ws.onmessage = async (msg) => {
        let frame;
        try {
          frame = JSON.parse(msg.data);
        } catch {
          return;
        }
        const { op, d } = frame ?? {};
        if (op === OP.HELLO) {
          const identify = {
            rpcVersion: 1,
            eventSubscriptions: EVENT_SUBSCRIPTIONS,
          };
          // The server only challenges when auth is enabled in OBS; a typed
          // password against an auth-less server is simply unused.
          if (d?.authentication) {
            identify.authentication = await buildAuthString(this.password, d.authentication);
          }
          this._send(OP.IDENTIFY, identify);
        } else if (op === OP.IDENTIFIED) {
          settled = true;
          clearTimeout(timer);
          resolve();
        } else if (op === OP.EVENT) {
          this.onEvent?.(d?.eventType, d?.eventData ?? {});
        } else if (op === OP.REQUEST_RESPONSE || op === OP.REQUEST_BATCH_RESPONSE) {
          this._settleRequest(d);
        }
      };

      ws.onclose = (ev) => {
        clearTimeout(timer);
        if (this._closed) return;
        this._closed = true;
        for (const [, pending] of this._pending) {
          clearTimeout(pending.timer);
          pending.reject(new Error('OBS connection closed'));
        }
        this._pending.clear();
        if (!settled) {
          settled = true;
          reject(new Error(`OBS connection closed (${ev.code})`));
        }
        this.onClose?.({ code: ev.code, reason: ev.reason });
      };
      // onerror always precedes onclose; the close handler is the single
      // teardown path, so nothing to do here.
      ws.onerror = () => {};
    });
  }

  get isOpen() {
    return !this._closed && this._ws?.readyState === WebSocket.OPEN;
  }

  // Resolves with responseData; rejects with an Error carrying .code /
  // .comment from the requestStatus on failure. Callers (obsControl) catch —
  // nothing here reaches the UI as an unhandled rejection.
  request(requestType, requestData = undefined) {
    return new Promise((resolve, reject) => {
      if (!this.isOpen) {
        reject(new Error('OBS is not connected'));
        return;
      }
      const requestId = newRequestId();
      const timer = setTimeout(() => {
        this._pending.delete(requestId);
        reject(new Error(`OBS request timed out (${requestType})`));
      }, REQUEST_TIMEOUT_MS);
      this._pending.set(requestId, { resolve, reject, timer });
      this._send(OP.REQUEST, {
        requestType,
        requestId,
        ...(requestData !== undefined ? { requestData } : {}),
      });
    });
  }

  // A request batch: OBS runs the requests in order (executionType; Sleep
  // requests wait inside it) and answers once with every result, so a batch
  // of transforms and sleeps plays as one animation whatever the console's
  // tab is doing. Resolves with the results (each { requestType,
  // requestStatus, responseData }); rejects as request() does. The timeout
  // allows for the sleeps.
  requestBatch(requests, { executionType = BATCH_SERIAL_REALTIME, haltOnFailure = false, timeoutMs = REQUEST_TIMEOUT_MS } = {}) {
    return new Promise((resolve, reject) => {
      if (!this.isOpen) {
        reject(new Error('OBS is not connected'));
        return;
      }
      const requestId = newRequestId();
      const timer = setTimeout(() => {
        this._pending.delete(requestId);
        reject(new Error('OBS request batch timed out'));
      }, timeoutMs);
      this._pending.set(requestId, { resolve, reject, timer, batch: true });
      this._send(OP.REQUEST_BATCH, {
        requestId,
        haltOnFailure,
        executionType,
        requests: requests.map(({ requestType, requestData }, i) => ({
          requestType,
          requestId: `${requestId}:${i}`,
          ...(requestData !== undefined ? { requestData } : {}),
        })),
      });
    });
  }

  close() {
    // Marks intent before closing so onclose still runs its cleanup exactly
    // once (the _closed flag flips there).
    try {
      this._ws?.close();
    } catch {
      /* already dead */
    }
  }

  _send(op, d) {
    if (this._ws?.readyState === WebSocket.OPEN) {
      this._ws.send(JSON.stringify({ op, d }));
    }
  }

  _settleRequest(d) {
    const pending = this._pending.get(d?.requestId);
    if (!pending) return;
    this._pending.delete(d.requestId);
    clearTimeout(pending.timer);
    if (pending.batch) {
      pending.resolve(Array.isArray(d?.results) ? d.results : []);
      return;
    }
    const status = d?.requestStatus ?? {};
    if (status.result) {
      pending.resolve(d?.responseData ?? {});
    } else {
      const err = new Error(status.comment || `OBS request failed (code ${status.code})`);
      err.code = status.code;
      err.comment = status.comment;
      pending.reject(err);
    }
  }
}
